import { Outlet, NavLink } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Button } from './ui/Button'
import { 
  Home, Gamepad2, Users, Trophy, Settings, LogOut,
  Flame
} from 'lucide-react'
import { clsx } from 'clsx'

export default function Layout() {
  const { user, profile, signOut } = useAuth()

  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/daily', icon: Gamepad2, label: 'Daily' },
    { path: '/duel', icon: Users, label: 'Duel' },
    { path: '/profile', icon: Trophy, label: 'Profile' },
    { path: '/leaderboard', icon: Flame, label: 'Leaderboard' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ]

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white/80 dark:bg-surface-950/80 backdrop-blur-md border-b border-surface-200 dark:border-surface-800">
        <div className="flex items-center justify-between px-4 py-3">
          <NavLink to="/" className="flex items-center gap-2 font-bold text-xl text-brand-600 dark:text-brand-400">
            <span className="w-8 h-8 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-black text-sm">O</span>
            </span>
            <span>Overlap</span>
          </NavLink>
          <Button variant="ghost" size="sm" onClick={signOut} className="lg:hidden">
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Mobile nav tabs */}
        <nav className="border-t border-surface-200 dark:border-surface-800 px-2 py-2">
          <div className="flex gap-1 overflow-x-auto pb-2">
            {navItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => clsx(
                  'flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap',
                  isActive
                    ? 'bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400'
                    : 'text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200'
                )}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 lg:z-30 bg-white dark:bg-surface-950 border-r border-surface-200 dark:border-surface-800">
          <div className="flex flex-col h-full">
            {/* Logo */}
            <NavLink to="/" className="flex items-center gap-3 px-6 py-5 font-bold text-xl text-brand-600 dark:text-brand-400 border-b border-surface-200 dark:border-surface-800">
              <span className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-black text-lg">O</span>
              </span>
              <span>Overlap</span>
            </NavLink>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
              {navItems.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => clsx(
                    'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400 shadow-sm'
                      : 'text-surface-500 hover:text-surface-700 hover:bg-surface-100 dark:text-surface-400 dark:hover:text-surface-200 dark:hover:bg-surface-800'
                  )}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>

            {/* User profile section */}
            <div className="p-4 border-t border-surface-200 dark:border-surface-800">
              {user ? (
                <div className="flex items-center gap-3 px-2 py-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white font-bold">
                    {profile?.display_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-surface-900 dark:text-surface-100 truncate">
                      {profile?.display_name || profile?.username || 'Player'}
                    </p>
                    <p className="text-xs text-surface-500 dark:text-surface-400">
                      Streak: {profile?.current_streak || 0} 🔥
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={signOut} className="p-1">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 min-h-screen">
          <div className="p-4 lg:p-6 xl:p-8 max-w-4xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}